import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface LeadContactInfo {
  id: number;
  fullName: string;
  name: string;
  lastName: string;
  phoneNumber: string;
  email?: string;
  campus?: {
    id: number;
    name: string;
    location?: string;
  };
  program?: {
    id: number;
    name: string;
  };
  owner?: {
    id: number;
    name: string;
    lastName: string;
    email: string;
  };
  company_id: number;
  status: string;
  profileImage?: string;
  lastContactDate?: Date;
  preferredContactMethod?: string;
  notes?: string;
}

export interface IncomingCallContext {
  lead?: LeadContactInfo;
  isKnownCaller: boolean;
  callHistory: {
    totalCalls: number;
    lastCallDate?: Date;
    averageDuration: number;
  };
  suggestedActions: string[];
}

@Injectable({
  providedIn: 'root'
})
export class LeadLookupService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/leads`;
  
  // Cache for frequent lookups
  private lookupCache = new Map<string, Observable<LeadContactInfo | null>>();
  private callContextCache = new Map<number, IncomingCallContext>();

  /**
   * Look up lead information by phone number
   */
  lookupByPhoneNumber(phoneNumber: string): Observable<LeadContactInfo | null> {
    // Check cache first
    const cacheKey = this.normalizePhoneNumber(phoneNumber);
    if (this.lookupCache.has(cacheKey)) {
      return this.lookupCache.get(cacheKey)!;
    }

    // Create new lookup request
    const lookup$ = this.http.get<{ data: LeadContactInfo }>(`${this.apiUrl}/lookup/phone/${encodeURIComponent(phoneNumber)}`)
      .pipe(
        map(response => response.data || null),
        tap(lead => {
          if (lead) {
          } else {
          }
        }),
        catchError(error => {
          console.warn('Lead lookup failed for phone number:', phoneNumber, error);
          return of(null);
        }),
        shareReplay(1)
      );

    // Cache the request
    this.lookupCache.set(cacheKey, lookup$);
    
    // Clear cache after 5 minutes
    setTimeout(() => {
      this.lookupCache.delete(cacheKey);
    }, 5 * 60 * 1000);

    return lookup$;
  }

  /**
   * Get comprehensive call context for incoming call
   */
  getIncomingCallContext(phoneNumber: string): Observable<IncomingCallContext> {
    return this.lookupByPhoneNumber(phoneNumber).pipe(
      map(lead => {
        const context: IncomingCallContext = {
          lead: lead || undefined,
          isKnownCaller: !!lead,
          callHistory: {
            totalCalls: 0,
            averageDuration: 0
          },
          suggestedActions: []
        };

        // Generate suggested actions based on lead data
        if (lead) {
          context.suggestedActions = this.generateSuggestedActions(lead);
          
          // If we have call history cached
          if (this.callContextCache.has(lead.id)) {
            const cachedContext = this.callContextCache.get(lead.id)!;
            context.callHistory = cachedContext.callHistory;
          }
        } else {
          context.suggestedActions = [
            'Create new lead',
            'Take detailed notes',
            'Ask for contact information'
          ];
        }

        return context;
      })
    );
  }

  /**
   * Load additional lead context (call history, notes)
   */
  loadLeadCallHistory(leadId: number): Observable<any> {
    return this.http.get(`${environment.apiUrl}/calls/history/lead/${leadId}?limit=10`)
      .pipe(
        map((response: any) => response.data || []),
        tap(history => {
          if (history.length > 0) {
            const context: IncomingCallContext = {
              lead: undefined,
              isKnownCaller: true,
              callHistory: {
                totalCalls: history.length,
                lastCallDate: new Date(history[0].startTime),
                averageDuration: history.reduce((sum: number, call: any) => sum + (call.duration || 0), 0) / history.length
              },
              suggestedActions: []
            };
            
            this.callContextCache.set(leadId, context);
          }
        }),
        catchError(error => {
          console.warn('Failed to load call history for lead:', leadId, error);
          return of([]);
        })
      );
  }

  /**
   * Get detailed lead information by ID
   */
  getLeadById(leadId: number): Observable<LeadContactInfo | null> {
    return this.http.get<{ data: LeadContactInfo }>(`${this.apiUrl}/${leadId}`)
      .pipe(
        map(response => response.data || null),
        catchError(error => {
          console.error('Failed to get lead by ID:', leadId, error);
          return of(null);
        })
      );
  }

  /**
   * Search leads by name or phone for quick lookup
   */
  searchLeads(query: string): Observable<LeadContactInfo[]> {
    return this.http.get<{ data: LeadContactInfo[] }>(`${this.apiUrl}/search?q=${encodeURIComponent(query)}&limit=10`)
      .pipe(
        map(response => response.data || []),
        catchError(error => {
          console.error('Lead search failed:', error);
          return of([]);
        })
      );
  }

  /**
   * Update lead with call outcome
   */
  updateLeadAfterCall(leadId: number, callOutcome: {
    disposition: string;
    notes?: string;
    duration: number;
    followUpRequired?: boolean;
    nextContactDate?: Date;
  }): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${leadId}/call-outcome`, callOutcome)
      .pipe(
        tap(() => {
          // Clear cache to force refresh
          this.clearLeadCache(leadId);
        }),
        catchError(error => {
          console.error('Failed to update lead after call:', error);
          return of(null);
        })
      );
  }

  /**
   * Normalize phone number for consistent caching
   */
  private normalizePhoneNumber(phoneNumber: string): string {
    // Remove all non-digit characters
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    // Handle different formats
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+${digitsOnly}`;
    } else if (digitsOnly.length === 9 && (digitsOnly.startsWith('69') || digitsOnly.startsWith('68') || digitsOnly.startsWith('67'))) {
      return `+355${digitsOnly}`;
    }
    
    // Return as-is for international numbers
    return phoneNumber.startsWith('+') ? phoneNumber : `+${digitsOnly}`;
  }

  /**
   * Generate suggested actions based on lead data
   */
  private generateSuggestedActions(lead: LeadContactInfo): string[] {
    const actions: string[] = [];
    
    // Status-based suggestions
    switch (lead.status?.toLowerCase()) {
      case 'new':
        actions.push('Conduct initial qualification', 'Schedule campus tour');
        break;
      case 'qualified':
        actions.push('Discuss program details', 'Schedule meeting with counselor');
        break;
      case 'interested':
        actions.push('Follow up on interest', 'Send program materials');
        break;
      case 'enrolled':
        actions.push('Check on progress', 'Address any concerns');
        break;
      default:
        actions.push('Update lead status', 'Record call notes');
    }

    // Program-based suggestions
    if (lead.program?.name) {
      actions.push(`Discuss ${lead.program.name} program`);
    }

    // Campus-based suggestions
    if (lead.campus?.name) {
      actions.push(`Mention ${lead.campus.name} campus benefits`);
    }

    // Time-based suggestions
    const now = new Date();
    const lastContact = lead.lastContactDate ? new Date(lead.lastContactDate) : null;
    if (lastContact) {
      const daysSinceContact = Math.floor((now.getTime() - lastContact.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceContact > 7) {
        actions.push('Check on recent developments');
      }
    }

    return actions.slice(0, 4); // Limit to top 4 suggestions
  }

  /**
   * Clear cached data for a specific lead
   */
  private clearLeadCache(leadId: number): void {
    // Clear call context cache
    this.callContextCache.delete(leadId);
    
    // Clear phone number lookup cache (we don't have the phone number here, so clear all)
    this.lookupCache.clear();
  }

  /**
   * Clear all cached data
   */
  clearAllCache(): void {
    this.lookupCache.clear();
    this.callContextCache.clear();
  }

  /**
   * Preload lead data for known phone numbers (optimization)
   */
  preloadLeadData(phoneNumbers: string[]): void {
    phoneNumbers.forEach(phoneNumber => {
      if (!this.lookupCache.has(this.normalizePhoneNumber(phoneNumber))) {
        this.lookupByPhoneNumber(phoneNumber).subscribe(); // Trigger the lookup
      }
    });
  }
}